import React from 'react';

import { AppState } from '../types/game';
import { useSession } from '../hooks/useSession';
import { ApiManager } from '../services/ApiManager';
import downIcon from '../assets/down.svg';
import closeIcon from '../assets/close.svg';
import './TitleBar.css';

interface TitleBarProps {
  title: string;
  timer?: string;
  appState: AppState;
  setAppState: (state: AppState) => void;
  location?: string;
  timeLimitInSeconds?: number;
  itemCount?: number;
  showCompactAfterCollapse?: boolean;
  isTransitioningToCompact?: boolean;
  onToggleExpand?: () => void;
}

const TitleBar: React.FC<TitleBarProps> = ({
  title,
  timer,
  appState,
  setAppState,
  location,
  timeLimitInSeconds,
  itemCount = 0,
  showCompactAfterCollapse = false,
  isTransitioningToCompact = false,
  onToggleExpand,
}) => {
  const { sessionId } = useSession();

  // Check if we're on product or rules page
  const isProductOrRulesPage = location?.includes('/product/') || location?.includes('/rules');

  // Show appropriate icon based on app state
  const icon = appState === 'collapsed' ? closeIcon : downIcon;
  const iconAlt = appState === 'collapsed' ? 'Close' : 'Down';

  // Helper function to calculate elapsed time (following same pattern as PlaymerceCartAddData)
  const getElapsedTime = (): number => {
    let elapsed = 0;
    try {
      const sessionStartTime = sessionStorage.getItem('playmerce_session_start_time');
      if (sessionStartTime) {
        const startTimestamp = parseInt(sessionStartTime, 10);
        elapsed = Math.floor((Date.now() - startTimestamp) / 1000);
      }
    } catch (error) {
      console.warn('Failed to calculate elapsed time:', error);
    }
    return elapsed;
  };

  // Handle whole title bar click - expand/collapse functionality
  const handleTitleBarClick = () => {
    // Don't allow expand/collapse during transition, in compact mode, or on product/rules pages
    if (showCompactAfterCollapse || isTransitioningToCompact || isProductOrRulesPage) return;

    // Toggle between expanded and collapsed
    const newState: AppState = appState === 'expanded' ? 'collapsed' : 'expanded';

    // Send analytics event when collapsing from expanded state
    if (appState === 'expanded' && newState === 'collapsed') {
      const sendAnalytics = async () => {
        try {
          await ApiManager.analytics.playmerce_collection_collapse(sessionId || undefined);
        } catch (error) {
          console.error('Failed to send collection collapse analytics:', error);
        }
      };
      sendAnalytics();
    }

    // Send analytics event when expanding from collapsed state
    if (appState === 'collapsed' && newState === 'expanded') {
      const sendAnalytics = async () => {
        try {
          await ApiManager.analytics.playmerce_collection_view({ item_count: itemCount }, sessionId || undefined);
        } catch (error) {
          console.error('Failed to send collection view analytics:', error);
        }
      };
      sendAnalytics();
    }

    setAppState(newState);

    // Call the callback if provided (for MainLayout to handle iframe messaging)
    if (onToggleExpand) {
      onToggleExpand();
    }
  };

  // Handle icon click - collapse when expanded, dismiss when collapsed
  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the click from bubbling to the container

    // Disable functionality on product/rules pages
    if (isProductOrRulesPage) {
      return;
    }

    if (appState === 'expanded') {
      // Send analytics event when collapsing from expanded state
      const sendAnalytics = async () => {
        try {
          await ApiManager.analytics.playmerce_collection_collapse(sessionId || undefined);
        } catch (error) {
          console.error('Failed to send collection collapse analytics:', error);
        }
      };
      sendAnalytics();

      setAppState('collapsed'); // Same effect as clicking the titlebar itself
    } else if (appState === 'collapsed') {
      // Send analytics event when dismissing from collapsed state
      const sendAnalytics = async () => {
        try {
          const elapsed = getElapsedTime();
          const timeLimit = timeLimitInSeconds || 0;

          await ApiManager.analytics.playmerce_session_dismiss(
            {
              time_limit: timeLimit,
              elapsed: elapsed,
            },
            sessionId || undefined
          );
        } catch (error) {
          console.error('Failed to send session dismiss analytics:', error);
        }
      };
      sendAnalytics();

      // Store dismiss state in localStorage
      localStorage.setItem('playmerce_dismissed', 'true');

      setAppState('dismissed'); // Only dismiss when already collapsed
    }
  };

  return (
    <div className="title-bar" onClick={handleTitleBarClick}>
      {appState === 'collapsed' && <h1 className="title-bar-text">{title}</h1>}
      <div className="title-bar-right">
        {appState === 'collapsed' && timer && <div className="title-bar-timer">{timer}</div>}
      </div>
      {!isProductOrRulesPage && (
        <button className="title-bar-button" onClick={handleIconClick}>
          <img src={icon} alt={iconAlt} />
        </button>
      )}
    </div>
  );
};

export default TitleBar;
